import { transformPhotoLibrarySections } from "./../../../utils/transformers/photoLibraryTransformer";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { iThunkApi } from "../../../types/commonTypes";
import { API_METHOD_TYPE, apiManager, isApiSuccess } from "../../apiManager";
import { API_ENDPOINTS } from "../../../constants/apiConstants";
import {
	IAddPhotoToSectionRequestPayload,
	IPhotoLibraryPhoto,
	IPhotoLibrarySection,
} from "../../../interfaces/photoLibrary";
import { VendorPhotoLibraryTypesEnum } from "../../../constants/enums";
import { getAccessToken } from "../../../helpers/authHelpers";
import { REACT_APP_CMS_URL } from "../../../constants/envConstants";
import { apiCommonManager } from "../../apiCommonManager";
import { iFilePayload, iFileResponse } from "../../../types/eventTypes";
import { AxiosProgressEvent } from "axios";
import { calculateUploadProgress } from "../../../helpers/common";

export const getPhotoLibrarySectionsThunk = createAsyncThunk<IPhotoLibrarySection[], void, iThunkApi>(
	"photo-library/sections",
	async (_, thunkAPI) => {
		const resp = await apiManager(API_ENDPOINTS.GET_ALL_SECTIONS, null, API_METHOD_TYPE.GET, {});
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}

		const transformedSections = transformPhotoLibrarySections(resp.data.data);

		return transformedSections;
	},
);

export const getPhotoLibraryPhotosThunk = createAsyncThunk<
	IPhotoLibraryPhoto[],
	{ sectionId: string },
	iThunkApi
>("photo-library/sections/{sectionId}", async ({ sectionId }, thunkAPI) => {
	const resp = await apiManager(
		API_ENDPOINTS.GET_PHOTOS_BY_SECTION_ID(sectionId),
		null,
		API_METHOD_TYPE.GET,
		{},
	);
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data.data as IPhotoLibraryPhoto[];
});

export const addNewSectionThunk = createAsyncThunk<IPhotoLibrarySection, { sectionName: string }, iThunkApi>(
	"photo-library/sections/add",
	async ({ sectionName }, thunkAPI) => {
		const resp = await apiManager(API_ENDPOINTS.ADD_NEW_SECTION, { sectionName }, API_METHOD_TYPE.POST);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data as IPhotoLibrarySection;
	},
);

export const updateSectionNameThunk = createAsyncThunk<
	IPhotoLibrarySection,
	{ sectionId: string; sectionName: string },
	iThunkApi
>("photo-library/sections/:id", async ({ sectionId, sectionName }, thunkAPI) => {
	const resp = await apiManager(
		API_ENDPOINTS.UPDATE_SECTION_NAME(sectionId),
		{ sectionName },
		API_METHOD_TYPE.PATCH,
	);
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data.data as IPhotoLibrarySection;
});

export const deleteSectionThunk = createAsyncThunk<void, { sectionId: string }, iThunkApi>(
	"photo-library/sections/:id",
	async ({ sectionId }, thunkAPI) => {
		const resp = await apiManager(API_ENDPOINTS.DELETE_SECTION(sectionId), null, API_METHOD_TYPE.DELETE);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data as void;
	},
);

export const addPhotoToSectionThunk = createAsyncThunk<
	IPhotoLibraryPhoto[],
	{
		sectionId: string;
		files: IAddPhotoToSectionRequestPayload[];
		onProgress?: (progress: number) => void;
	},
	iThunkApi
>("photo-library/sections/:sectionId/photos", async ({ sectionId, files, onProgress }, thunkAPI) => {
	const resp = await apiManager(
		API_ENDPOINTS.ADD_PHOTO_TO_SECTION(sectionId),
		{ files },
		API_METHOD_TYPE.POST,
		undefined,
		undefined,
		undefined,
		onProgress
			? (progressEvent: AxiosProgressEvent) => {
					const percent = calculateUploadProgress(progressEvent);
					onProgress?.(percent);
				}
			: undefined,
	);
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data.data as IPhotoLibraryPhoto[];
});

export const addPhotoToSystemSectionThunk = createAsyncThunk<
	IPhotoLibraryPhoto[],
	{ files: IAddPhotoToSectionRequestPayload[]; onProgress?: (progress: number) => void },
	iThunkApi
>("photo-library/vendor/photos", async ({ files, onProgress }, thunkAPI) => {
	const resp = await apiManager(
		API_ENDPOINTS.ADD_PHOTO_TO_SYSTEM_SECTION(VendorPhotoLibraryTypesEnum.Uncategorized),
		{ files },
		API_METHOD_TYPE.POST,
		undefined,
		undefined,
		undefined,
		onProgress
			? (progressEvent: AxiosProgressEvent) => {
					const percent = calculateUploadProgress(progressEvent);
					onProgress?.(percent);
				}
			: undefined,
	);
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data.data as IPhotoLibraryPhoto[];
});

export const deletePhotoFromGalleryThunk = createAsyncThunk<void, { photoId: string }, iThunkApi>(
	"photo-library/photos/:id",
	async ({ photoId }, thunkAPI) => {
		const resp = await apiManager(
			API_ENDPOINTS.DELETE_PHOTO_FROM_GALLERY(photoId),
			null,
			API_METHOD_TYPE.DELETE,
		);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data as void;
	},
);

export const uploadPhotoToS3Thunk = createAsyncThunk<iFileResponse, iFilePayload, iThunkApi>(
	"photo-library/photos/upload-to-s3",
	async ({ file, directory, onProgress }, thunkAPI) => {
		const formData = new FormData();

		if (file) {
			formData.append("file", file!);
		}
		formData.append("directory", directory);

		const resp = await apiCommonManager.request(
			API_ENDPOINTS.UPLOAD_FILE,
			formData,
			"POST",
			getAccessToken(),
			REACT_APP_CMS_URL,
			true,
			undefined,
			(e: AxiosProgressEvent) => {
				const percent = calculateUploadProgress(e);
				onProgress?.(percent);
			},
		);
		if (resp.data.status === false) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data;
	},
);

export const unlinkPhotoFromSectionThunk = createAsyncThunk<
	IPhotoLibraryPhoto,
	{ photoId: string },
	iThunkApi
>("photo-library/photos/:id/unlink-section", async ({ photoId }, thunkAPI) => {
	const resp = await apiManager(
		API_ENDPOINTS.UNLINK_PHOTO_FROM_SECTION(photoId),
		null,
		API_METHOD_TYPE.PATCH,
	);
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data.data as IPhotoLibraryPhoto;
});
